import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, ArrowLeft, Search, Copy, Trash2, Eye, Edit3, Rocket, Archive, Clock } from "lucide-react";
import {
  adminListListings, adminPatchListing, adminTrashListing, adminDuplicateListing,
} from "../../lib/adminApi";

const TABS = [
  { key: "all",       label: "All" },
  { key: "draft",     label: "Drafts" },
  { key: "published", label: "Published" },
  { key: "featured",  label: "Featured" },
  { key: "archived",  label: "Archived" },
];

export default function ListingsIndex() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [type, setType] = useState("");
  const [params, setParams] = useSearchParams();
  const tab = params.get("status") === "published" ? "published"
           : params.get("status") === "archived" ? "archived"
           : params.get("status") === "draft" ? "draft"
           : params.get("featured") === "1" ? "featured"
           : "all";
  const nav = useNavigate();

  const load = async () => {
    try { setRows(await adminListListings()); }
    catch (e) { toast.error("Could not load listings"); }
  };
  useEffect(() => { load(); }, []);

  const setTab = (key) => {
    const p = new URLSearchParams();
    if (key === "published") p.set("status","published");
    else if (key === "archived") p.set("status","archived");
    else if (key === "draft") p.set("status","draft");
    else if (key === "featured") p.set("featured","1");
    setParams(p);
  };

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (tab === "draft"     && r.status !== "draft") return false;
      if (tab === "published" && r.status !== "published") return false;
      if (tab === "archived"  && r.status !== "archived") return false;
      if (tab === "featured"  && !r.featured) return false;
      if (region && r.region_slug !== region) return false;
      if (type && r.listing_type !== type) return false;
      if (q) {
        const hay = `${r.name_en || ""} ${r.name_ro || ""} ${r.city || ""} ${r.region_slug || ""} ${r.slug || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, tab, region, type, q]);

  const regions = useMemo(() => Array.from(new Set(rows.map(r => r.region_slug).filter(Boolean))).sort(), [rows]);
  const types = useMemo(() => Array.from(new Set(rows.map(r => r.listing_type).filter(Boolean))).sort(), [rows]);

  const setStatus = async (r, status) => {
    try { await adminPatchListing(r.id, { status }); toast.success(status); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const toggleFeatured = async (r) => {
    try { await adminPatchListing(r.id, { featured: !r.featured }); load(); }
    catch { toast.error("Update failed"); }
  };
  const dup = async (r) => { try { await adminDuplicateListing(r.id); toast.success("Duplicated"); load(); } catch { toast.error("Duplicate failed"); } };
  const trash = async (r) => {
    if (!window.confirm(`Move "${r.name_en || r.name_ro || r.slug}" to Trash?\n\nIt will be hidden from the public site but recoverable from the Trash view.`)) return;
    try { await adminTrashListing(r.id); toast.success("Moved to Trash"); load(); } catch { toast.error("Trash failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="publisher-listings">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="inline-flex items-center gap-2 font-mono-wr text-[10px] uppercase tracking-widest hover:text-[var(--wr-red)]" data-testid="back-dashboard">
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <div>
            <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)]">/ PUBLISHER</p>
            <h1 className="font-editorial text-4xl mt-1">Listings</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/trash" className="inline-flex items-center gap-2 px-4 py-3 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid="open-trash">
            <Trash2 size={14} /> Trash
          </Link>
          <Link to="/admin/new" className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-ink)] font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid="new-listing-btn-top">
            <PlusCircle size={16} /> New listing
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="listings-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`px-3 py-2 border font-mono-wr text-[10px] uppercase tracking-widest ${tab===t.key?"bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]":"border-[var(--wr-border)] hover:border-[var(--wr-ink)]"}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[240px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--wr-ink-2)]" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search name, city, slug…"
            className="w-full border border-[var(--wr-border)] bg-[var(--wr-surface)] pl-9 pr-3 py-2.5 focus:outline-none focus:border-[var(--wr-ink)]" data-testid="listings-search" />
        </div>
        <select value={region} onChange={(e)=>setRegion(e.target.value)} className="border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2.5" data-testid="listings-filter-region">
          <option value="">All regions</option>
          {regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={type} onChange={(e)=>setType(e.target.value)} className="border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2.5" data-testid="listings-filter-type">
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="border border-[var(--wr-border)] overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="listings-table">
          <thead className="text-left border-b border-[var(--wr-border)] bg-[var(--wr-surface)]">
            <tr className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">
              <th className="py-3 pl-4 pr-3">Name</th>
              <th className="pr-3">Type</th>
              <th className="pr-3">Region</th>
              <th className="pr-3">Status</th>
              <th className="pr-3">★</th>
              <th className="pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-[var(--wr-border)] hover:bg-[var(--wr-surface)]" data-testid={`listings-row-${r.slug}`}>
                <td className="py-3 pl-4 pr-3">
                  <Link to={`/admin/edit/${r.id}`} className="block">
                    <div className="font-editorial text-lg leading-tight">{r.name_en || <span className="text-[var(--wr-ink-2)]">Untitled</span>}</div>
                    <div className="text-[var(--wr-ink-2)] text-[12px]">{r.name_ro}</div>
                  </Link>
                </td>
                <td className="pr-3 font-mono-wr text-[11px]">{r.listing_type || "—"}</td>
                <td className="pr-3 font-mono-wr text-[11px]">{r.region_slug || "—"}</td>
                <td className="pr-3">
                  <span className={`font-mono-wr text-[10px] uppercase tracking-widest px-2 py-1 border ${r.status==="published"?"border-emerald-700 text-emerald-700":r.status==="archived"?"border-[var(--wr-ink-2)] text-[var(--wr-ink-2)]":"border-[var(--wr-red)] text-[var(--wr-red)]"}`}>{r.status}</span>
                </td>
                <td className="pr-3">
                  <button onClick={()=>toggleFeatured(r)} className="w-6 h-6 flex items-center justify-center" title={r.featured?"Unfeature":"Feature"} data-testid={`toggle-featured-${r.slug}`}>
                    {r.featured ? <span className="text-[var(--wr-red)]">★</span> : <span className="text-[var(--wr-ink-2)]">☆</span>}
                  </button>
                </td>
                <td className="pr-4 py-3 text-right space-x-1 whitespace-nowrap">
                  <Link to={`/admin/edit/${r.id}`} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-edit-${r.slug}`}><Edit3 size={11}/>Edit</Link>
                  <Link to={`/admin/preview/${r.id}`} target="_blank" className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-preview-${r.slug}`}><Eye size={11}/>Preview</Link>
                  {r.status !== "published" && <button onClick={()=>setStatus(r,"published")} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-publish-${r.slug}`}><Rocket size={11}/>Publish</button>}
                  {r.status === "published" && <button onClick={()=>setStatus(r,"draft")} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-unpublish-${r.slug}`}>Unpublish</button>}
                  {r.status !== "archived" && <button onClick={()=>setStatus(r,"archived")} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-archive-${r.slug}`}><Archive size={11}/>Archive</button>}
                  <button onClick={()=>dup(r)} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-duplicate-${r.slug}`}><Copy size={11}/>Duplicate</button>
                  <Link to={`/admin/history/${r.id}`} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px]" data-testid={`row-history-${r.slug}`} title="History"><Clock size={11}/>History</Link>
                  <button onClick={()=>trash(r)} className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--wr-red)] text-[var(--wr-red)] hover:bg-[var(--wr-red)] hover:text-white font-mono-wr text-[10px]" data-testid={`row-trash-${r.slug}`} title="Move to Trash"><Trash2 size={11}/>Trash</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-16 text-center" data-testid="listings-empty">
            <p className="font-editorial text-2xl mb-2">Nothing here yet.</p>
            <p className="text-[13px] text-[var(--wr-ink-2)] mb-6">Try a different tab or filter, or start a new listing.</p>
            <Link to="/admin/new" className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] font-mono-wr text-[10px] uppercase tracking-widest">
              <PlusCircle size={14}/> New listing
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
